import { useState } from 'react';
import Section from '../components/Section';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.status === 200) {
        setStatus({
          type: 'success',
          message: 'Thank you for your enquiry. A member of the Summit team will be in touch shortly.'
        });
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: ''
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'There was an error submitting your enquiry. Please try again or contact us directly.'
      });
      console.error('Contact form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Page Header */}
      <section className="bg-summit-navy-dark text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold">Contact Summit MCG</h1>
          <p className="text-xl text-gray-200 mt-4">
            Start the conversation about improving your dealership performance
          </p>
        </div>
      </section>

      <Section>
        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <h2 className="text-3xl font-bold text-summit-navy-dark mb-6">Get in Touch</h2>
            <p className="text-summit-grey leading-relaxed mb-8">
              Whether you're exploring consultancy support, management development, or sales event programmes, we're here to discuss your requirements.
            </p>
            
            <div className="space-y-6 mb-8">
              <div>
                <h3 className="font-semibold text-summit-navy mb-2">Email</h3>
                <a href="mailto:Contact@summitmcg.co.uk" className="text-summit-grey hover:text-summit-navy">
                  Contact@summitmcg.co.uk
                </a>
              </div>
              
              <div>
                <h3 className="font-semibold text-summit-navy mb-2">WhatsApp</h3>
                <a href="https://wa.me/447432206795" className="text-summit-grey hover:text-summit-navy" target="_blank" rel="noopener noreferrer">
                  07432 206 795
                </a>
              </div>
              
              <div>
                <h3 className="font-semibold text-summit-navy mb-2">Website</h3>
                <p className="text-summit-grey">summitmcg.co.uk</p>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <h3 className="font-semibold text-summit-navy mb-2">Company Details</h3>
              <p className="text-sm text-summit-grey mb-1">Summit Motor Consultancy Group Limited</p>
              <p className="text-sm text-summit-grey mb-1">Company Number: 16917041</p>
              <p className="text-sm text-summit-grey mb-1">Registered Office: 20 Wenlock Road, London, N1 7GU</p>
              <p className="text-sm text-summit-grey">Registered in England & Wales</p>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <h2 className="text-2xl font-bold text-summit-navy-dark mb-6">Send Us a Message</h2>
            
            {status.message && (
              <div className={`mb-6 p-4 rounded-sm ${
                status.type === 'success' 
                  ? 'bg-green-50 text-green-800 border border-green-200' 
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                {status.message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-summit-navy mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-summit-navy focus:border-transparent"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-summit-navy mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-summit-navy focus:border-transparent"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-semibold text-summit-navy mb-2">
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-summit-navy focus:border-transparent"
                  data-testid="contact-company-input"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-summit-navy mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-summit-navy focus:border-transparent"
                  data-testid="contact-phone-input"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-summit-navy mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-summit-navy focus:border-transparent"
                  data-testid="contact-message-input"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 py-3 bg-summit-navy text-white font-semibold rounded-sm hover:bg-summit-navy-dark transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </Section>
    </>
  );
};

export default Contact;
